import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { ChartBarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../../api';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { motion } from 'framer-motion';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const ErrorStatusGraph = ({ filters }) => {
  const [focus, setFocus] = useState(false);
  const { accessToken } = useAuth();

  const [months, setMonths] = useState([]);
  const [accepted, setAccepted] = useState([]);
  const [notAccepted, setNotAccepted] = useState([]);
  const [falseCounts, setFalseCounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAccepted, setShowAccepted] = useState(true);
  const [showRejected, setShowRejected] = useState(true);
  const [showFalse, setShowFalse] = useState(true);

  useEffect(() => {
    const fetchErrorData = async () => {
      try {
        setLoading(true);
        const baseUrl = getRoundRobinBaseUrl();

        const filteredParams = {};
        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) filteredParams[key] = filters[key];
        });

        const response = await api.get(
          `${baseUrl}/superAdminDashboard/getAuditRebuttalPercentage`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: filteredParams,
          }
        );

        if (response.data.success && response.data.data?.auditStatusPercentage) {
          const dataset = response.data.data.auditStatusPercentage;
          setMonths(dataset.map((item) => item.month));
          setAccepted(dataset.map((item) => item.RebuttalAcceptanceCount || 0));
          setNotAccepted(dataset.map((item) => item.RebuttalNotAcceptanceCount || 0));
          setFalseCounts(dataset.map((item) => item.RebuttalExistsFalseCount || 0));
        } else {
          console.error('Invalid API response format');
        }
      } catch (err) {
        console.error('Failed to fetch rebuttal data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (filters) fetchErrorData();
  }, [accessToken, filters]);

  const noData =
    !months.length ||
    (accepted.every((val) => val === 0) &&
      notAccepted.every((val) => val === 0) &&
      falseCounts.every((val) => val === 0));

  const datasets = [];
  if (showAccepted)
    datasets.push({
      label: 'Rebuttal Accepted',
      data: accepted,
      fill: true,
      borderColor: '#10B981',
      backgroundColor: (ctx) => {
        const gradient = ctx.chart.ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, 'rgba(16,185,129,0.4)');
        gradient.addColorStop(1, 'rgba(16,185,129,0.05)');
        return gradient;
      },
      pointRadius: 4,
      pointHoverRadius: 8,
      pointBackgroundColor: '#10B981',
      borderWidth: 2,
      tension: 0.4,
    });

  if (showRejected)
    datasets.push({
      label: 'Rebuttal Not Accepted',
      data: notAccepted,
      fill: true,
      borderColor: '#EF4444',
      backgroundColor: (ctx) => {
        const gradient = ctx.chart.ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, 'rgba(239,68,68,0.4)');
        gradient.addColorStop(1, 'rgba(239,68,68,0.05)');
        return gradient;
      },
      pointRadius: 4,
      pointHoverRadius: 8,
      pointBackgroundColor: '#EF4444',
      borderWidth: 2,
      tension: 0.4,
    });

  if (showFalse)
    datasets.push({
      label: 'Rebuttal False',
      data: falseCounts,
      fill: true,
      borderColor: '#3B82F6',
      borderDash: [5, 5],
      backgroundColor: (ctx) => {
        const gradient = ctx.chart.ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, 'rgba(59,130,246,0.3)');
        gradient.addColorStop(1, 'rgba(59,130,246,0.05)');
        return gradient;
      },
      pointRadius: 4,
      pointHoverRadius: 8,
      pointBackgroundColor: '#3B82F6',
      borderWidth: 2,
      tension: 0.4,
    });

  const data = { labels: months, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}`,
        },
        backgroundColor: '#fff',
        titleColor: '#111827',
        bodyColor: '#1F2937',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        padding: 10,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        ticks: { color: '#374151' },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { color: '#6B7280' },
        grid: {
          color: '#E5E7EB',
          borderDash: [4, 4],
        },
      },
    },
  };

  const toggleDataset = (label) => {
    if (label === 'Accepted') setShowAccepted((prev) => !prev);
    if (label === 'Rejected') setShowRejected((prev) => !prev);
    if (label === 'False') setShowFalse((prev) => !prev);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="rounded-3xl p-8 shadow-xl backdrop-blur-md overflow-hidden bg-white border border-gray-200 text-[#1A2B5C]"
      >
        <div className="flex justify-between items-center mb-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <ChartBarIcon className="w-6 h-6 text-[#EE1D23]" />
              <h2 className="text-xl font-bold uppercase tracking-wider">Monthly Rebuttal Status</h2>
            </div>
            <div className="h-[3px] w-24 bg-gradient-to-r from-[#EE1D23] to-[#1A2B5C] rounded-full" />
          </div>
          <button
            onClick={() => setFocus(true)}
            className="px-4 py-1.5 border border-[#1A2B5C] rounded-full text-sm font-medium hover:bg-[#1A2B5C] hover:text-white transition"
          >
            Focus Mode
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          {['Accepted', 'Rejected', 'False'].map((label) => (
            <button
              key={label}
              onClick={() => toggleDataset(label)}
              className={`px-3 py-1.5 text-xs rounded-full font-medium border ${
                (label === 'Accepted' && showAccepted) ||
                (label === 'Rejected' && showRejected) ||
                (label === 'False' && showFalse)
                  ? 'bg-[#1A2B5C] text-white'
                  : 'border-gray-300 text-gray-500'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="h-[280px] relative">
          {loading ? (
            <div className="flex justify-center items-center h-full text-gray-400">Loading...</div>
          ) : noData ? (
            <div className="flex justify-center items-center h-full text-gray-500 text-sm">
              No Data Found
            </div>
          ) : (
            <Line data={data} options={options} />
          )}
        </div>
      </motion.div>

      {/* Focus Mode Modal */}
      {focus && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative w-full max-w-6xl h-[90vh] p-8 rounded-3xl overflow-auto shadow-2xl bg-white text-[#1A2B5C] border border-gray-300">
            <button
              onClick={() => setFocus(false)}
              className="absolute top-5 right-5 bg-white shadow-md rounded-full p-2 text-gray-600 hover:text-[#EE1D23] hover:shadow-lg"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold tracking-wide">Monthly Rebuttal Status (Focus Mode)</h2>
            </div>
            <div className="h-[65vh]">
              <Line data={data} options={options} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ErrorStatusGraph;
